import { getAppConfigSingleton } from "@expert/app-config";
import { useToast } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { shallow } from "zustand/shallow";
import { useVerifiedCustomerStore } from "@/customer-info";
import { updateCase } from "@/customer-info/api/updateCase";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useAgentSdk, useAgentStore, useCallbackState, usePartner, useSession } from "@/sdk";
import { getHashedValue, removeCountryCode, useFeatureFlag } from "@/shared-utils";
import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { useSessionSummaryDispatchActions } from "../components/hooks";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { EditSessionSummaryModal } from "./EditSessionSummaryModal";
import styles from "./EditSessionSummaryModal.module.css";
import { adaptSimplifiedSummary } from "./utils";

const logger = getLogger({ module: "SessionSummary", supportTeam: "solve" });

// TODO: Rename this component because it's not deprecated, and actually used by every partner except Verizon
export function DeprecatedSessionSummary(): JSX.Element | null {
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();

    const {
        mdn,
        accountNumber,
        metadata: { caseNumber, caseId } = {},
    } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(activeSession.id).customer ?? {});
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const { accountNumberLength, wrappingAgentActivity, gaiaSummaryModel } = appConfig.getPartnersConfig(partner);
    const { simplifiedSummary } = usePartnerFeatureConfigured();
    const { data: isAutoSummaryEnabled } = useFeatureFlag("isAutoSummaryEnabled", { partner });

    const validAccountNumber = simplifiedSummary
        ? !!caseId
        : accountNumberLength
          ? accountNumber?.length === accountNumberLength
          : false;

    const { dispatchSessionSummaryStats, dispatchSessionSubmit } = useSessionSummaryDispatchActions();

    const [setCanAppend, setSubmitted, autogeneratedSummary] = useSessionSummaryStore((state) => [
        state.setCanAppend,
        state.setSubmitted,
        state.autogeneratedSummary,
    ]);

    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);

    const toast = useToast();
    const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);

    const agentActivity = useAgentStore(useShallow((state) => state.activity));
    const callbackState = useCallbackState();

    const {
        summary,
        submitted,
        setSessionSummary,
        setSubject,
        subject,
        category,
        subcategory,
        setCategory,
        setSubcategory,
        resolutionStatus,
    } = useSessionSummaryStore((state) => state, shallow);
    const activeTask = activeSession.currentTask;
    const expirationTimestamp = activeSession.wrappingState?.expirationTimestamp;

    const agentCallSid =
        activeTask && isVoiceTask(activeTask)
            ? (activeTask.agentCallId ??
              Array.from(activeSession.tasks.values())
                  .filter(isVoiceTask)
                  .findLast((task) => task.agentCallId !== undefined)?.agentCallId)
            : undefined;

    const shouldAutoGenerateSummary = activeTask?.locale !== "es-mx" && !!gaiaSummaryModel;

    // A wrapping task or a newly wrapping agent means we are entering session summary
    const inWrappingState = activeTask?.status === "wrapping" || agentActivity === wrappingAgentActivity;

    const taskIsActive = activeTask?.status === "assigned";
    const hasImmediateCallBack = callbackState?.callbackType === "CallbackNow";

    const handleSimplifiedEndSession = async () => {
        try {
            if (!caseId) {
                await navigator.clipboard.writeText(summary);
                toast.success("Summary copied to clipboard");
                return;
            }
            setSendingSummary(true);
            await updateCase({
                caseId,
                subject,
                description: summary,
                category,
                subcategory,
            });
            toast.success("Summary sent");
            setSubmitted(true);
        } catch (err) {
            logger.warn({ err }, "SessionSummary | Error sending summary");
            toast.error("Error sending summary. Please copy the summary manually.");
        } finally {
            setSendingSummary(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    const setSessionSummaryFromGaia = useCallback(async () => {
        logger.trace(
            { agentCallSid, sessionId: activeSession.id, partner },
            "SessionSummary | Gaia summary fetch check",
        );
        const generatedSummary: string = agentCallSid
            ? await getGaiaSummary({ callSid: agentCallSid, sessionId: activeSession.id, partner }, gaiaSummaryModel)
            : await getBackupGaiaSummary({ sessionId: activeSession.id, partner }, gaiaSummaryModel);

        if (!generatedSummary) {
            logger.warn("SessionSummary | Received empty summary from Gaia");
        } else if (simplifiedSummary) {
            const { subject, summary, category, subcategory } = adaptSimplifiedSummary(generatedSummary);
            setSessionSummary({ value: summary, autoGenerated: true });
            setSubject(subject);
            if (category) setCategory(category);
            if (subcategory) setSubcategory(subcategory);
        } else {
            setSessionSummary({ value: generatedSummary, autoGenerated: true });
        }
        setSummaryLoaded(true);
    }, [
        agentCallSid,
        activeSession.id,
        partner,
        setSessionSummary,
        setSummaryLoaded,
        gaiaSummaryModel,
        setSubject,
        simplifiedSummary,
        setSubcategory,
        setCategory,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (!shouldAutoGenerateSummary) {
            setSummaryLoaded(true);
            return;
        }

        if (opened && inWrappingState && !summaryLoaded) {
            if (autogeneratedSummary && !submitted && simplifiedSummary) {
                setSummaryLoaded(true);
                if (caseId) {
                    void handleSimplifiedEndSession();
                }
            } else if (!autogeneratedSummary || !summaryLoaded) {
                // NOTE: The summary loaded check is to prevent getting stuck in the loading state if we've already generated a summary from a previous leg of the call
                void setSessionSummaryFromGaia();
            }
        }
    }, [inWrappingState, shouldAutoGenerateSummary, opened, autogeneratedSummary, submitted, simplifiedSummary]);

    useEffect(() => {
        if (hasImmediateCallBack) return;

        if (inWrappingState) {
            openModal();
        } else if (taskIsActive) {
            setCanAppend(true);
        }
    }, [hasImmediateCallBack, inWrappingState, taskIsActive, setCanAppend, openModal]);

    const shouldSendSummary = !submitted && summary;

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    const onSessionSummaryCompleted = useCallback(
        async (update: string, shouldSendToPartnerTool?: boolean) => {
            if (!update) {
                logger.error("SessionSummary | summary is undefined, unable to send analytics");
                toast.error("No summary provided");
                closeModal();
                return;
            }

            const extraSessionSummaryPayloadProps =
                appConfig.getPartnersConfig(partner).extraSessionSummaryPayloadProps;

            const sessionSummaryPayload = {
                accountNo: accountNumber,
                remarkText: update,
                resolutionStatus,
                ...extraSessionSummaryPayloadProps,
                ...(category ? { category } : {}),
                ...(subcategory ? { subcategory } : {}),
            };

            const hasValidMdn = !!mdn;
            if (hasValidMdn) {
                // Summary -> EIP. If accountNumber exists it will also be sent to partner tool (ACSS/OPUS)
                const mdnFormatted = removeCountryCode(mdn);
                const mdnHashed = await getHashedValue(mdnFormatted);
                dispatchSessionSubmit({ agentCallSid, mdn: mdnFormatted, mdnHashed, sessionSummaryPayload });
            }

            dispatchSessionSummaryStats({
                agentCallSid,
                update,
                autogeneratedSummary,
                expirationTimestamp,
                summaryWasCopiedToClipboard: !shouldSendToPartnerTool,
                summaryWasSubmittedViaPartnerTool: shouldSendToPartnerTool,
                gaiaSummaryModel,
                hasValidMdn,
            });

            setSubmitted(true);
            if (shouldSendToPartnerTool) {
                if (hasValidMdn) toast.success("Summary sent");
                else toast.error("Summary NOT sent - MDN was not verified");
            } else {
                toast.success("Summary copied to clipboard");
            }
            closeModal();
        },
        [
            mdn,
            accountNumber,
            setSubmitted,
            partner,
            closeModal,
            toast,
            dispatchSessionSummaryStats,
            summary,
            dispatchSessionSubmit,
            agentCallSid,
            autogeneratedSummary,
            expirationTimestamp,
            appConfig.getPartnersConfig,
            validAccountNumber,
            gaiaSummaryModel,
            category,
            subcategory,
            resolutionStatus,
        ],
    );

    async function handleWrappingExpired() {
        await handleEndSession("WrappingTimeExpired");
    }

    const handleCopyAndSendSummary = useCallback(async () => {
        async function writeToClipboard() {
            try {
                await navigator.clipboard.writeText(summary);
            } catch (err) {
                logger.warn({ err }, "SessionSummary | Error copying session summary");
            }
        }
        await Promise.all([writeToClipboard(), onSessionSummaryCompleted(summary, false)]);
    }, [summary, onSessionSummaryCompleted]);

    const handleEndSession = useCallback(
        async (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => {
            if (validAccountNumber && isAutoSummaryEnabled) await onSessionSummaryCompleted(summary, true);
            else await handleCopyAndSendSummary();
            logger.trace({ reason, partner }, "SessionSummary | Ending session wrapup");
            if (activeTask) {
                await agentSdk.endWrapup(reason, partner, activeTask);
            }
        },
        [
            activeTask,
            agentSdk,
            handleCopyAndSendSummary,
            isAutoSummaryEnabled,
            onSessionSummaryCompleted,
            partner,
            summary,
            validAccountNumber,
        ],
    );

    const sendSummary = useCallback(async () => {
        dispatchSessionSummaryStats({
            agentCallSid,
            update: summary,
            autogeneratedSummary,
            expirationTimestamp: 0,
            timeRanOut: true,
            summaryWasCopiedToClipboard: true,
            summaryWasSubmittedViaPartnerTool: false,
        });

        toast.success("Session summary copied to clipboard");
        try {
            await navigator.clipboard.writeText(summary);
        } catch (err) {
            logger.warn({ err }, "SessionSummary | Error copying session summary");
        }
    }, [agentCallSid, autogeneratedSummary, dispatchSessionSummaryStats, summary, toast]);

    const onDismiss = useCallback(
        async ({ checkSummary = true } = {}) => {
            logger.trace("SessionSummary | Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                await sendSummary();
            }
        },
        [closeModal, sendSummary, shouldSendSummary],
    );

    return (
        <Group className={styles.tempTimeline}>
            <EditSessionSummaryModal
                loading={!summaryLoaded && shouldAutoGenerateSummary}
                onDismiss={simplifiedSummary ? () => {} : () => void onDismiss()}
                sendingSummary={sendingSummary}
                onExpired={simplifiedSummary ? () => {} : handleWrappingExpired}
                simplifiedSummary={simplifiedSummary}
                caseNumber={caseNumber}
                validAccountNumber={validAccountNumber}
                endSession={simplifiedSummary ? handleSimplifiedEndSession : handleEndSession}
                opened={opened}
            />
        </Group>
    );
}
